// Library Methods
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import SummaryCard from "./SummaryCard";
import { FigureRow, Figures } from "./FigureRow";

// Utils
import { num } from "./formatters";

type HeadsetFitCardType = {
  // How far the eye tracker's own check missed by, in degrees.
  residualDegrees: number | null | undefined;
  // Whether that residual clears what the section needs. Carried rather than compared here --
  // the floor is the exam's number and the exam already classified against it.
  qualityFloorMet: boolean | null | undefined;
  interpupillaryDistanceMetres: number | null | undefined;
  interpupillaryDistanceMeasured: boolean | null | undefined;
  // Whether the headset sat inside the range its optics were characterised over. Only the
  // Aniseikonia section measures this, so it is optional and the figure is omitted without it.
  withinCharacterisedFitRange?: boolean | null;
};

/**
 * How well the headset was sitting and how well the eye tracker was working, which is the same
 * card on every section that has one.
 *
 * It leads the report rather than closing it. Everything under it is a statement about the
 * patient that is only worth reading if this card is good -- a tracker that missed by a degree
 * or a headset sitting outside the range its optics were characterised over changes what the
 * numbers mean, and a reader who finds that out at the bottom of the page has already believed
 * the top of it.
 */
const HeadsetFitCard = ({
  residualDegrees,
  qualityFloorMet,
  interpupillaryDistanceMetres,
  interpupillaryDistanceMeasured,
  withinCharacterisedFitRange,
}: HeadsetFitCardType) => {
  const { t } = useTranslation();

  return (
    <SummaryCard title={t("Headset Fit and Eye Tracking")} cautions={null}>
      <Box sx={{ pb: "8px" }}>
        <Figures>
          <FigureRow
            label={t("Eye Tracking Accuracy")}
            value={num(residualDegrees, 2, "°")}
            sub={
              qualityFloorMet
                ? t("Meets what this exam needs")
                : t("Below what this exam needs")
            }
          />
          <FigureRow
            label={t("IPD")}
            value={num(
              interpupillaryDistanceMetres != null
                ? interpupillaryDistanceMetres * 1000
                : null,
              1,
              " mm"
            )}
            sub={
              interpupillaryDistanceMeasured
                ? t("Distance between the pupils, measured by the headset")
                : t("Distance between the pupils, entered by hand")
            }
          />
          {withinCharacterisedFitRange !== undefined &&
            withinCharacterisedFitRange !== null && (
              <FigureRow
                label={t("Headset Fit")}
                value={withinCharacterisedFitRange ? t("Good") : t("Check")}
                sub={
                  withinCharacterisedFitRange
                    ? t("Within the range the headset is characterized for")
                    : t("Outside the range the headset is characterized for")
                }
              />
            )}
        </Figures>
      </Box>
    </SummaryCard>
  );
};

export default HeadsetFitCard;
